/* ==========================================================================
   Superboba One-Time Bundle LP — JavaScript
   Single-step bundle builder (no subscription): flavor selection with a
   quantity-discount progress bar → checkout at one-time prices.
   Cloned mechanics from try.javvycoffee.com/pc-ot; shares CSS + markup
   patterns with sb-lp-funnel.js (the subscription multi-step LP).
   ========================================================================== */

(function () {
  'use strict';

  /* ========================================================================
     STATE
     ======================================================================== */
  const state = {
    flavors: {},             // { variantId: quantity }
    totalSelected: 0,
    carouselIndex: 0,
    carouselCount: 0,
  };

  /* ========================================================================
     DOM REFERENCES
     ======================================================================== */
  let offerSection;
  let carouselImages, carouselThumbs, carouselDots, prevBtn, nextBtn;
  let flavorCards, giftBoxes, giftTitles;
  let checkoutCta, checkoutCtaText, priceOriginalEl, priceSaleEl;
  let progNodes, progFill;

  /* ========================================================================
     PRICING TIERS (progress bar nodes)
     Each node carries data-quantity / data-unit-price (dollars) /
     data-discount-code. The tier for a given bag count is the exact match,
     falling back to the highest tier for counts beyond the ladder.
     ======================================================================== */
  function getTierForQuantity(qty) {
    if (!progNodes || !progNodes.length) return null;
    var exact = null, highest = null, highestQty = 0;
    progNodes.forEach(function (n) {
      var q = parseInt(n.dataset.quantity) || 0;
      if (q === qty) exact = n;
      if (q > highestQty) { highestQty = q; highest = n; }
    });
    return exact || (qty > highestQty ? highest : null);
  }

  function minQuantity() {
    var min = Infinity;
    if (progNodes) progNodes.forEach(function (n) {
      var q = parseInt(n.dataset.quantity) || 0;
      if (q > 0 && q < min) min = q;
    });
    return min === Infinity ? 1 : min;
  }

  /* ========================================================================
     EAGER CART CLEAR — same trick as the subscription funnel:
     /cart/clear.js measures 2-3s on this store, so fire it on the FIRST
     flavor interaction and reuse the promise at checkout time.
     ======================================================================== */
  var cartClearPromise = null;
  function ensureCartCleared() {
    if (!cartClearPromise) {
      cartClearPromise = fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }).catch(function () { /* non-fatal */ });
    }
    return cartClearPromise;
  }
  function invalidateCartClear() { cartClearPromise = null; }

  /* ========================================================================
     CAROUSEL (same markup as the funnel LP)
     ======================================================================== */
  function initCarousel() {
    const main = offerSection?.querySelector('.sb-funnel__carousel-main');
    if (!main) return;
    carouselImages = main.querySelectorAll('img');
    state.carouselCount = carouselImages.length;
    carouselThumbs = offerSection.querySelectorAll('.sb-funnel__carousel-thumb');
    carouselDots = offerSection.querySelectorAll('.sb-funnel__carousel-dot');
    prevBtn = offerSection.querySelector('.sb-funnel__carousel-arrow--prev');
    nextBtn = offerSection.querySelector('.sb-funnel__carousel-arrow--next');

    carouselThumbs.forEach(function (t, i) { t.addEventListener('click', function () { setCarouselIndex(i); }); });
    carouselDots.forEach(function (d, i) { d.addEventListener('click', function () { setCarouselIndex(i); }); });
    if (prevBtn) prevBtn.addEventListener('click', function () { setCarouselIndex(state.carouselIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { setCarouselIndex(state.carouselIndex + 1); });

    let startX = 0;
    main.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    main.addEventListener('touchend', function (e) {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) setCarouselIndex(state.carouselIndex + (diff > 0 ? 1 : -1));
    }, { passive: true });

    setCarouselIndex(0);
  }

  function setCarouselIndex(idx) {
    if (idx < 0) idx = 0;
    if (idx >= state.carouselCount) idx = state.carouselCount - 1;
    state.carouselIndex = idx;
    carouselImages.forEach(function (img, i) { img.classList.toggle('active', i === idx); });
    carouselThumbs.forEach(function (t, i) { t.classList.toggle('active', i === idx); });
    carouselDots.forEach(function (d, i) { d.classList.toggle('active', i === idx); });
    if (prevBtn) prevBtn.disabled = idx === 0;
    if (nextBtn) nextBtn.disabled = idx === state.carouselCount - 1;
  }

  /* ========================================================================
     FLAVOR QUANTITY CONTROLS
     ======================================================================== */
  function initFlavors() {
    flavorCards = offerSection?.querySelectorAll('.sb-funnel__flavor');
    if (!flavorCards) return;

    flavorCards.forEach(function (card) {
      var variantId = card.dataset.variantId;
      var addBtn = card.querySelector('.sb-funnel__add-btn');
      var qtyBox = card.querySelector('.sb-funnel__qty-box');
      var qtyDisplay = card.querySelector('.sb-funnel__qty-display');
      var plusBtn = card.querySelector('.sb-funnel__qty-plus');
      var minusBtn = card.querySelector('.sb-funnel__qty-minus');

      if (addBtn) {
        addBtn.addEventListener('click', function () {
          ensureCartCleared(); // slow call starts in the background on first intent
          state.flavors[variantId] = 1;
          addBtn.style.display = 'none';
          if (qtyBox) qtyBox.classList.add('visible');
          if (qtyDisplay) qtyDisplay.textContent = '1';
          card.classList.add('sb-funnel__flavor--active');
          recalcTotal();
        });
      }
      if (plusBtn) {
        plusBtn.addEventListener('click', function () {
          state.flavors[variantId] = (state.flavors[variantId] || 0) + 1;
          if (qtyDisplay) qtyDisplay.textContent = state.flavors[variantId];
          recalcTotal();
        });
      }
      if (minusBtn) {
        minusBtn.addEventListener('click', function () {
          state.flavors[variantId] = (state.flavors[variantId] || 0) - 1;
          if (state.flavors[variantId] <= 0) {
            delete state.flavors[variantId];
            if (addBtn) addBtn.style.display = '';
            if (qtyBox) qtyBox.classList.remove('visible');
            card.classList.remove('sb-funnel__flavor--active');
          } else {
            if (qtyDisplay) qtyDisplay.textContent = state.flavors[variantId];
          }
          recalcTotal();
        });
      }
    });
  }

  function recalcTotal() {
    state.totalSelected = 0;
    for (var key in state.flavors) state.totalSelected += state.flavors[key];
    updateProgress();
    updateGifts();
    updateCta();
  }

  /* ========================================================================
     PROGRESS BAR — fills toward the best deal as bags are added
     ======================================================================== */
  function updateProgress() {
    if (!progNodes || !progNodes.length) return;
    var maxQty = 0;
    progNodes.forEach(function (n) { maxQty = Math.max(maxQty, parseInt(n.dataset.quantity) || 0); });

    progNodes.forEach(function (n) {
      var q = parseInt(n.dataset.quantity) || 0;
      n.classList.toggle('sb-ot__prog-node--reached', state.totalSelected >= q);
      n.classList.toggle('sb-ot__prog-node--current',
        q === Math.min(state.totalSelected, maxQty) && state.totalSelected > 0);
    });

    if (progFill && maxQty > 1) {
      var capped = Math.min(state.totalSelected, maxQty);
      var pct = capped <= 1 ? 0 : ((capped - 1) / (maxQty - 1)) * 100;
      progFill.style.width = pct + '%';
    }
  }

  /* ========================================================================
     GIFT TIER PROGRESS (unlock by bag count, step-1-style boxes)
     ======================================================================== */
  function initGifts() {
    giftBoxes = offerSection?.querySelectorAll('.sb-funnel__gift');
    giftTitles = offerSection?.querySelectorAll('.sb-funnel__gift-title');
  }

  function updateGifts() {
    if (!giftBoxes) return;
    giftBoxes.forEach(function (box, i) {
      var threshold = parseInt(box.dataset.unlockAt) || 1;
      var unlocked = state.totalSelected >= threshold;
      box.classList.toggle('sb-funnel__gift--unlocked', unlocked);
      if (giftTitles && giftTitles[i]) {
        giftTitles[i].classList.toggle('sb-funnel__gift-title--active', unlocked);
      }
    });
  }

  /* ========================================================================
     CTA + DYNAMIC PRICING
     Sale total   = bag count × the matched tier's per-unit price.
     Struck total = bag count × full unit price (per-flavor data-price)
                    + the value of every unlocked gift (the Javvy framing:
                    "here's everything you'd pay full price for").
     ======================================================================== */
  function money(n) { return '$' + n.toFixed(2); }

  function updateCta() {
    if (!checkoutCta) return;
    var min = minQuantity();

    if (state.totalSelected < min) {
      checkoutCta.disabled = true;
      checkoutCta.classList.remove('sb-funnel__cta--ready');
      if (checkoutCtaText) {
        checkoutCtaText.textContent = 'Select at least ' + min + ' bag' + (min === 1 ? '' : 's');
      }
      if (priceOriginalEl) priceOriginalEl.textContent = '';
      if (priceSaleEl) priceSaleEl.textContent = '';
      return;
    }

    var tier = getTierForQuantity(state.totalSelected);
    var unitPrice = tier ? parseFloat(tier.dataset.unitPrice) : NaN;

    // Full-price total from each flavor's variant price (cents)
    var fullTotal = 0;
    for (var variantId in state.flavors) {
      var card = offerSection.querySelector('.sb-funnel__flavor[data-variant-id="' + variantId + '"]');
      var cents = card ? parseInt(card.dataset.price) : 0;
      fullTotal += (cents / 100) * state.flavors[variantId];
    }
    // Add unlocked gift values to the struck price
    var boxes = giftBoxes || [];
    boxes.forEach(function (box) {
      var threshold = parseInt(box.dataset.unlockAt) || 1;
      var val = parseFloat(box.dataset.giftValue);
      if (!isNaN(val) && state.totalSelected >= threshold) fullTotal += val;
    });

    var saleTotal = !isNaN(unitPrice) ? unitPrice * state.totalSelected : fullTotal;

    checkoutCta.disabled = false;
    checkoutCta.classList.add('sb-funnel__cta--ready');
    checkoutCta.classList.remove('sb-funnel__cta--loading');
    checkoutCta.dataset.processing = 'false';
    if (checkoutCtaText) checkoutCtaText.textContent = 'Checkout Now';
    if (priceOriginalEl) priceOriginalEl.textContent = fullTotal > saleTotal ? money(fullTotal) : '';
    if (priceSaleEl) priceSaleEl.textContent = money(saleTotal);
  }

  /* ========================================================================
     DEBUG PANEL (?debug=1)
     ======================================================================== */
  function isDebug() {
    try { return new URLSearchParams(window.location.search).get('debug') === '1'; }
    catch (e) { return false; }
  }
  function debugStatus(msg, isError) {
    if (!isDebug()) return;
    var el = document.getElementById('sb-funnel-debug');
    if (!el) {
      el = document.createElement('div');
      el.id = 'sb-funnel-debug';
      el.style.cssText = 'position:fixed;bottom:0;left:0;right:0;background:#111;color:#fff;font:12px/1.3 monospace;padding:8px 12px;z-index:999999;max-height:40vh;overflow:auto;white-space:pre-wrap;word-break:break-all';
      document.body.appendChild(el);
    }
    var line = document.createElement('div');
    line.style.color = isError ? '#ff6b6b' : '#b2f5b2';
    line.textContent = new Date().toLocaleTimeString() + ' ' + msg;
    el.appendChild(line);
    el.scrollTop = el.scrollHeight;
  }

  /* ========================================================================
     CHECKOUT — one-time items (NO selling plan), gifts best-effort,
     tier + gift discount codes via a single /checkout?discount= navigation.
     Same 429 retry / recovery / timing plumbing as the subscription funnel.
     ======================================================================== */
  function checkout() {
    var flavorItems = [];
    for (var variantId in state.flavors) {
      if (state.flavors[variantId] > 0) {
        flavorItems.push({ id: parseInt(variantId), quantity: state.flavors[variantId] });
      }
    }
    if (flavorItems.length === 0) return;

    var giftItems = [];
    (giftBoxes || []).forEach(function (box) {
      var threshold = parseInt(box.dataset.unlockAt) || 1;
      var giftVariant = box.dataset.giftVariant;
      if (giftVariant && state.totalSelected >= threshold) {
        giftItems.push({ id: parseInt(giftVariant), quantity: 1, properties: { '_gift': 'true' } });
      }
    });

    if (checkoutCta) {
      checkoutCta.disabled = true;
      checkoutCta.classList.remove('sb-funnel__cta--ready');
      checkoutCta.classList.add('sb-funnel__cta--loading');
    }
    function setStageText(txt) { if (checkoutCtaText) checkoutCtaText.textContent = txt; }
    setStageText('Building your order...');

    var _t = { start: performance.now(), addRetries: 0, phases: {} };
    function _mark(k) { _t.phases[k] = Math.round(performance.now() - _t.start); }
    function _emitTiming(extra) {
      var payload = {
        slug: funnelSlug, phases: _t.phases, addRetries: _t.addRetries,
        codes: extra && extra.codes != null ? extra.codes : undefined,
        path: extra && extra.path, totalMs: Math.round(performance.now() - _t.start)
      };
      try { console.log('[sb-ot][timing]', JSON.stringify(payload)); } catch (e) {}
      try { sessionStorage.setItem('sb-funnel-timing', JSON.stringify(payload)); } catch (e) {}
    }

    var recoveryTimeout = setTimeout(resetCtaState, 15000);
    function clearRecovery() { clearTimeout(recoveryTimeout); }
    function resetCtaState() {
      clearRecovery();
      if (checkoutCta) {
        checkoutCta.disabled = false;
        checkoutCta.classList.remove('sb-funnel__cta--loading');
        checkoutCta.classList.add('sb-funnel__cta--ready');
        checkoutCta.dataset.processing = 'false';
      }
      updateCta();
    }

    var ADD_MAX_ATTEMPTS = 4;
    function addBatch(items, attempt) {
      attempt = attempt || 1;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        if (res.ok) return res.json();
        if (res.status === 429 && attempt < ADD_MAX_ATTEMPTS) {
          _t.addRetries++;
          var base = 400 * Math.pow(2, attempt - 1);
          var delay = Math.round(base * (0.75 + Math.random() * 0.5));
          debugStatus('429 on /cart/add — retry ' + attempt + ' in ' + delay + 'ms', true);
          return new Promise(function (resolve) {
            setTimeout(function () { resolve(addBatch(items, attempt + 1)); }, delay);
          });
        }
        return res.text().then(function (body) {
          var err = new Error('HTTP ' + res.status + ': ' + body.slice(0, 200));
          err.status = res.status;
          throw err;
        });
      });
    }
    function addAllItems() {
      invalidateCartClear();
      var combined = flavorItems.concat(giftItems);
      return addBatch(combined, 1).catch(function () {
        // fallback: flavors required, gifts best-effort
        return addBatch(flavorItems, 1).then(function (data) {
          if (giftItems.length === 0) return data;
          return Promise.all(giftItems.map(function (gift) {
            return addBatch([gift], 1).catch(function () {});
          })).then(function () { return data; });
        });
      });
    }

    var funnelSlug = offerSection.dataset.funnelSlug || 'mt-ot-01';
    var offerSource = offerSection.dataset.offerSource || 'paid-offer-onetime';
    function setCartAttributes() {
      var attrs = {
        tier: String(state.totalSelected),
        source: offerSource,
        purchase_type: 'one-time'
      };
      attrs['multi-step-lp'] = funnelSlug;
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).catch(function () {});
    }

    function collectCodes() {
      var codes = [], seen = {};
      function pushCode(c) {
        if (!c) return;
        var t = c.trim();
        if (!t || seen[t]) return;
        seen[t] = true;
        codes.push(t);
      }
      var tier = getTierForQuantity(state.totalSelected);
      pushCode(tier && tier.dataset.discountCode);
      (giftBoxes || []).forEach(function (box) {
        var threshold = parseInt(box.dataset.unlockAt) || 1;
        if (state.totalSelected >= threshold) pushCode(box.dataset.giftDiscountCode);
      });
      pushCode(offerSection.dataset.giftDiscount);
      return codes;
    }

    console.log('[sb-ot] checkout flow start, slug=' + funnelSlug);
    ensureCartCleared()
      .then(function () {
        _mark('clear');
        return Promise.all([addAllItems(), setCartAttributes()]);
      })
      .then(function () {
        _mark('addUpdate');
        setStageText('Activating your savings...');
        var codes = collectCodes();
        clearRecovery();
        if (codes.length === 0) {
          _emitTiming({ codes: 0, path: 'no-codes' });
          window.location.href = '/checkout';
          return;
        }
        var nav = '/checkout?discount=' + codes.map(encodeURIComponent).join(',');
        console.log('[sb-ot] navigate →', nav);
        _emitTiming({ codes: codes.length, path: 'checkout-discount-param' });
        window.location.href = nav;
      })
      .catch(function (err) {
        console.error('[sb-ot] checkout failed:', err);
        debugStatus('EXCEPTION: ' + (err && err.message ? err.message : String(err)), true);
        resetCtaState();
      });
  }

  /* ========================================================================
     INIT
     ======================================================================== */
  function init() {
    offerSection = document.getElementById('sb-ot-offer');
    if (!offerSection) return;

    checkoutCta = offerSection.querySelector('[data-action="ot-checkout"]');
    checkoutCtaText = checkoutCta?.querySelector('.sb-funnel__cta-text');
    priceOriginalEl = checkoutCta?.querySelector('.sb-funnel__cta-price-original');
    priceSaleEl = checkoutCta?.querySelector('.sb-funnel__cta-price-sale');
    progNodes = offerSection.querySelectorAll('.sb-ot__prog-node');
    progFill = offerSection.querySelector('[data-prog-fill]');

    if (checkoutCta) {
      checkoutCta.addEventListener('click', function () {
        if (checkoutCta.disabled) return;
        if (checkoutCta.dataset.processing === 'true') return;
        checkoutCta.dataset.processing = 'true';
        checkout();
      });
    }

    initCarousel();
    initGifts();
    initFlavors();
    updateProgress();
    updateGifts();
    updateCta();
  }

  function resetState() {
    state.flavors = {};
    state.totalSelected = 0;
    invalidateCartClear();
    if (flavorCards) {
      flavorCards.forEach(function (card) {
        card.classList.remove('sb-funnel__flavor--active');
        var addBtn = card.querySelector('.sb-funnel__add-btn');
        var qtyBox = card.querySelector('.sb-funnel__qty-box');
        var qtyDisplay = card.querySelector('.sb-funnel__qty-display');
        if (addBtn) addBtn.style.display = '';
        if (qtyBox) qtyBox.classList.remove('visible');
        if (qtyDisplay) qtyDisplay.textContent = '1';
      });
    }
    updateProgress();
    updateGifts();
    updateCta();
  }

  // Only reset on bfcache restore (back from checkout) — see funnel JS notes.
  window.addEventListener('pageshow', function (event) {
    if (!offerSection) return;
    if (event.persisted) resetState();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
